import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { InsuranceFlowStateHistoryStatus } from 'src/app/_enums/insurance-flow/insurance-flow-history-status.enum';
import { InsuranceFlowStatus } from 'src/app/_enums/insurance-flow/insurance-flow-status.enum';
import { InsuranceFlowStateHistory } from 'src/app/_types/insurance-flow.types';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { StepperComponent } from 'src/app/shared/ui-kit/stepper/stepper.component';
import {
  UiStepperState,
  UiStepperStep,
  UiStepperSubStep,
} from 'src/app/shared/ui-kit/stepper/stepper.types';

import { TaskDetailsService } from '../../task-details.service';
import { TASK_DETAILS_STEP_CONFIG } from './constants';
import { AcceptOfferStepperActionComponent } from './stepper-actions/accept-offer/accept-offer.component';
import { ActivatePolicyStepperActionComponent } from './stepper-actions/activate-policy/activate-policy.component';
import { ArchivePolicyStepperActionComponent } from './stepper-actions/archive-policy/archive-policy.component';
import { CancelContractStepperActionComponent } from './stepper-actions/cancel-contract/cancel-contract.component';
import { CancelLeadStepperActionComponent } from './stepper-actions/cancel-lead/cancel-lead.component';
import { CancelPolicyStepperActionComponent } from './stepper-actions/cancel-policy/cancel-policy.component';
import { CreatePolicyStepperActionComponent } from './stepper-actions/create-policy/create-policy.component';
import { CreateProposalStepperActionComponent } from './stepper-actions/create-proposal/create-proposal.component';
import { MarkPolicyInactiveStepperActionComponent } from './stepper-actions/mark-policy-inactive/mark-policy-inactive.component';
import { RefuseContractStepperActionComponent } from './stepper-actions/refuse-contract/refuse-contract.component';
import { SendLeadActionComponent } from './stepper-actions/send-lead/send-lead.component';
import { SendProposalsActionComponent } from './stepper-actions/send-proposals/send-proposals.component';
import { SendSignedProposalComponent } from './stepper-actions/send-signed-proposal/send-signed-proposal.component';
import { SuspendPolicyStepperActionComponent } from './stepper-actions/suspend-policy/suspend-policy.component';
import { UpdateLeadStepperActionComponent } from './stepper-actions/update-lead/update-lead.component';

@Component({
  selector: 'app-task-history',
  standalone: true,
  imports: [CommonModule, StepperComponent, SpinnerComponent],
  templateUrl: './task-history.component.html',
  styleUrl: './task-history.component.css',
})
export class TaskHistoryComponent implements OnInit, OnDestroy {
  protected flowSteps: UiStepperStep[] = [];

  protected isLoading = false;

  private stateSubscription?: Subscription;

  constructor(private readonly taskDetailsService: TaskDetailsService) {}

  ngOnInit(): void {
    this.stateSubscription = this.taskDetailsService.state$.subscribe(
      (state) => {
        this.isLoading = state.isLoading;
        this.flowSteps = state.insuranceFlowData?.status
          ? this.buildFlowSteps(
              state.insuranceFlowData.status,
              state.insuranceFlowData.statusHistory ?? []
            )
          : [];
      }
    );
  }

  ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
  }

  private getStepActionConfig(
    status: InsuranceFlowStatus
  ): Pick<UiStepperSubStep, 'action' | 'actionComponent'> {
    switch (status) {
      case InsuranceFlowStatus.PENDING_LEAD:
        return {
          actionComponent: {
            component: CreateProposalStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.LEAD_SENT_TO_INSURER:
        return {
          actionComponent: {
            component: SendLeadActionComponent,
          },
        };
      case InsuranceFlowStatus.LEAD_UPDATED:
        return {
          actionComponent: {
            component: UpdateLeadStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.LEAD_CANCELLED:
        return {
          actionComponent: {
            component: CancelLeadStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.PREPARING_OFFERS:
        return {
          actionComponent: {
            component: SendProposalsActionComponent,
          },
        };
      case InsuranceFlowStatus.PENDING_OFFERS:
        return {
          actionComponent: {
            component: AcceptOfferStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.PENDING_CONTRACT:
        return {
          actionComponent: {
            component: SendSignedProposalComponent,
          },
        };
      case InsuranceFlowStatus.PENDING_VALIDATION:
        return {
          actionComponent: {
            component: CreatePolicyStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.CONTRACT_CANCELLED:
        return {
          actionComponent: {
            component: CancelContractStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.CONTRACT_REFUSED:
        return {
          actionComponent: {
            component: RefuseContractStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.POLICY_ARCHIVED:
        return {
          actionComponent: {
            component: ArchivePolicyStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.POLICY_CANCELLED:
        return {
          actionComponent: {
            component: CancelPolicyStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.POLICY_SUSPENDED:
        return {
          actionComponent: {
            component: SuspendPolicyStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.POLICY_ACTIVE:
        return {
          actionComponent: {
            component: ActivatePolicyStepperActionComponent,
          },
        };
      case InsuranceFlowStatus.POLICY_INACTIVE:
        return {
          actionComponent: {
            component: MarkPolicyInactiveStepperActionComponent,
          },
        };
      default:
        return {};
    }
  }

  private buildFlowSteps(
    currentStatus: InsuranceFlowStatus,
    statusHistory: InsuranceFlowStateHistory[]
  ): UiStepperStep[] {
    const historyStatuses = new Set(statusHistory.map((entry) => entry.status));
    const currentStepIndex = TASK_DETAILS_STEP_CONFIG.findIndex((step) =>
      step.subSteps.some((subStep) => subStep.status === currentStatus)
    );

    return TASK_DETAILS_STEP_CONFIG.map((step, stepIndex) => {
      const mappedSubSteps = step.subSteps.map((subStep) => {
        const isActive = subStep.status === currentStatus;
        const isDone = historyStatuses.has(subStep.status) && !isActive;
        const statusEntry = this.findLatestStatusEntry(statusHistory, [
          subStep.status,
        ]);
        const { action, actionComponent } = this.getStepActionConfig(
          subStep.status
        );

        return {
          id: subStep.id,
          label: subStep.label,
          caption: this.buildHistoryCaption(statusEntry),
          required: subStep.required,
          state: this.resolveSubStepState(isActive, isDone, currentStatus),
          action,
          actionComponent,
        } as const;
      });

      const hasActiveSubStep = mappedSubSteps.some(
        (subStep) => subStep.state === 'active' || subStep.state === 'error'
      );
      const hasCompletedSubStep = mappedSubSteps.some(
        (subStep) => subStep.state === 'completed'
      );

      return {
        id: step.id,
        label: step.label,
        state: this.resolveStepState(
          hasActiveSubStep,
          hasCompletedSubStep,
          stepIndex,
          currentStepIndex
        ),
        subSteps: mappedSubSteps,
      };
    });
  }

  private resolveSubStepState(
    isActive: boolean,
    isDone: boolean,
    currentStatus: InsuranceFlowStatus
  ): UiStepperState {
    const shouldRenderAsError =
      (currentStatus === InsuranceFlowStatus.LEAD_CANCELLED ||
        currentStatus === InsuranceFlowStatus.CONTRACT_CANCELLED ||
        currentStatus === InsuranceFlowStatus.CONTRACT_REFUSED ||
        currentStatus === InsuranceFlowStatus.POLICY_CANCELLED) &&
      isActive;
    if (shouldRenderAsError) {
      return 'error';
    }

    if (isActive) {
      return 'active';
    }

    if (isDone) {
      return 'completed';
    }

    return 'pending';
  }

  private resolveStepState(
    hasActiveSubStep: boolean,
    hasCompletedSubStep: boolean,
    stepIndex: number,
    currentStepIndex: number
  ): UiStepperState {
    if (hasActiveSubStep) {
      return 'active';
    }

    if (hasCompletedSubStep) {
      return 'completed';
    }

    if (currentStepIndex !== -1 && stepIndex < currentStepIndex) {
      return 'completed';
    }

    return 'pending';
  }

  private findLatestStatusEntry(
    statusHistory: InsuranceFlowStateHistory[],
    statuses: InsuranceFlowStatus[]
  ): InsuranceFlowStateHistory | undefined {
    const acceptedStatuses = new Set<InsuranceFlowStateHistoryStatus>(statuses);

    return statusHistory
      .filter((entry) => acceptedStatuses.has(entry.status))
      .sort(
        (a, b) =>
          new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime()
      )[0];
  }

  private buildHistoryCaption(
    entry?: InsuranceFlowStateHistory
  ): string | undefined {
    if (!entry) {
      return undefined;
    }

    const date = this.formatHistoryDate(entry.endedOn);
    const fullName = entry.endedByFullName?.trim();

    if (fullName && date) {
      return `${fullName} ${date}`;
    }

    return fullName || date || undefined;
  }

  private formatHistoryDate(updatedOn: string): string {
    const date = new Date(updatedOn);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(date);
  }
}
